import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  apiError,
  commentFinding,
  createCapa,
  getActiveCatalogue,
  getEntityAudit,
  getFinding,
  transitionFinding,
  updateFinding,
} from "../services/AiService";
import {
  AcesCatalogue,
  AuditEntry,
  CLASSIFICATIONS,
  Classification,
  EVIDENCE_STATUSES,
  EvidenceStatus,
  FindingAction,
  FindingDetail,
  IMPACTS,
  ProductIntegrityImpact,
} from "../types/ai";
import { assessRisk, isUrgent } from "../utils/risk";
import {
  AiNotice,
  AuditTable,
  CapaChip,
  ClassificationChip,
  ErrorAlert,
  Loading,
  PageShell,
  ReasonModal,
  ReviewChip,
  RiskChip,
  SecureImage,
  UrgentBanner,
  fmtDate,
} from "../component/aces/Shared";

interface EditState {
  classification: Classification;
  evidenceStatus: EvidenceStatus;
  likelihood: number | null;
  severity: number | null;
  statement: string;
  applicableRequirement: string;
  objectiveEvidence: string;
  process: string;
  productIntegrityImpact: ProductIntegrityImpact | null;
  productIntegrityDescription: string;
  immediateContainment: string;
  recommendedAction: string;
  capaRequired: boolean;
  cargoJourneyStageId: number | null;
  ccpId: number | null;
  acesClauseId: number | null;
  correctiveAction: string;
  preventiveAction: string;
}

const fromDetail = (d: FindingDetail): EditState => ({
  classification: d.finding.classification,
  evidenceStatus: d.finding.evidenceStatus,
  likelihood: d.finding.likelihood,
  severity: d.finding.severity,
  statement: d.finding.statement,
  applicableRequirement: d.finding.applicableRequirement || "",
  objectiveEvidence: d.finding.objectiveEvidence || "",
  process: d.finding.process || "",
  productIntegrityImpact: d.finding.productIntegrityImpact,
  productIntegrityDescription: d.finding.productIntegrityDescription || "",
  immediateContainment: d.finding.immediateContainment || "",
  recommendedAction: d.finding.recommendedAction || "",
  capaRequired: d.finding.capaRequired,
  cargoJourneyStageId: d.finding.cargoJourneyStageId,
  ccpId: d.finding.ccpId,
  acesClauseId: d.finding.acesClauseId,
  correctiveAction: d.capaProposal?.corrective_action || "",
  preventiveAction: d.capaProposal?.preventive_action || "",
});

/** Translation keys for revision field names. */
const FIELD_LABEL: Record<string, string> = {
  classification: "aces.finding.classification", likelihood: "aces.finding.likelihood", severity: "aces.finding.severity",
  riskScore: "aces.finding.riskScore", riskLevel: "aces.finding.riskLevel", evidenceStatus: "aces.finding.evidenceStatus",
  statement: "aces.finding.statement", applicableRequirement: "aces.finding.requirement", objectiveEvidence: "aces.finding.objectiveEvidence",
  process: "aces.ctx.process", productIntegrityImpact: "aces.finding.productIntegrity", productIntegrityDescription: "aces.finding.productIntegrityDescription",
  immediateContainment: "aces.finding.containment", recommendedAction: "aces.finding.recommendedAction", capaRequired: "aces.finding.capaRequired",
  possibleCauses: "aces.finding.possibleCauses", confirmationsNeeded: "aces.finding.confirmations", capaProposal: "aces.finding.capaProposal",
  cargoJourneyStageId: "aces.finding.stage", ccpId: "aces.finding.ccp", acesClauseId: "aces.finding.acesReference",
};
/** Enum family used to translate a revision value. */
const FIELD_ENUM: Record<string, string> = {
  classification: "classification", riskLevel: "risk", evidenceStatus: "evidence", productIntegrityImpact: "impact",
};

/** Fields whose current value differs from the AI proposal (keys of the AI JSON). */
const AI_KEYS: Record<string, string> = {
  classification: "classification", evidenceStatus: "evidence_status", likelihood: "likelihood", severity: "severity",
  statement: "finding_statement", applicableRequirement: "applicable_requirement", objectiveEvidence: "objective_evidence",
  immediateContainment: "immediate_containment", recommendedAction: "recommended_action", capaRequired: "capa_required",
};

const FindingScreen = () => {
  const { id } = useParams();
  const findingId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [detail, setDetail] = useState<FindingDetail | null>(null);
  const [catalogue, setCatalogue] = useState<AcesCatalogue | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [pending, setPending] = useState<FindingAction | null>(null);
  const [comment, setComment] = useState("");
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await getFinding(findingId));
      setError(null);
    } catch (e) {
      setError(apiError(e));
    }
  }, [findingId]);

  useEffect(() => {
    load();
    getActiveCatalogue().then(setCatalogue).catch(() => setCatalogue(null));
  }, [load]);

  const changes = useMemo(() => {
    if (!detail || !edit) return {};
    const base = fromDetail(detail);
    const out: Record<string, any> = {};
    (Object.keys(edit) as (keyof EditState)[]).forEach((k) => {
      if (k === "correctiveAction" || k === "preventiveAction") return;
      const a = edit[k] === "" ? null : edit[k];
      const b = base[k] === "" ? null : base[k];
      if (a !== b) out[k] = a;
    });
    if (edit.correctiveAction !== base.correctiveAction || edit.preventiveAction !== base.preventiveAction) {
      out.capaProposal = { ...(detail.capaProposal || {}), corrective_action: edit.correctiveAction || null, preventive_action: edit.preventiveAction || null };
    }
    return out;
  }, [detail, edit]);

  if (!detail) {
    return (
      <PageShell title={t("aces.titles.finding")} crumbs={[{ label: t("aces.nav.findings"), to: "/ai/findings" }, { label: t("aces.titles.findingCrumb") }]}>
        <ErrorAlert message={error} />
        {!error && <Loading />}
      </PageShell>
    );
  }

  const f = detail.finding;
  // A closed finding keeps the decision taken before closing.
  const decision = f.reviewStatus === "CLOSED"
    ? [...detail.history].reverse().find((h) => h.toStatus !== "CLOSED")?.toStatus
    : f.reviewStatus;
  const rejected = decision === "REJECTED";
  const approved = decision === "ACCEPTED" || decision === "MODIFIED";
  const ai = detail.aiOriginal || {};
  const preview = edit ? assessRisk(edit.likelihood, edit.severity) : { score: f.riskScore, level: f.riskLevel };
  const previewUrgent = edit ? isUrgent(edit.classification, preview.level) : f.isUrgent;
  const changedFromAi = (field: string, current: any) => {
    if (field === "__changed") return true;
    const key = AI_KEYS[field];
    if (!key || !detail.aiOriginal) return false;
    const original = ai[key];
    return (original ?? null) !== (current === "" ? null : current ?? null);
  };

  const run = async (action: FindingAction, reason?: string) => {
    setBusy(true);
    try {
      setDetail(await transitionFinding(findingId, action, reason));
      setPending(null);
    } catch (e) {
      setError(apiError(e));
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  const save = async (reason: string) => {
    try {
      setDetail(await updateFinding(findingId, changes, reason, f.version));
      setEdit(null);
      setSaveOpen(false);
    } catch (e) {
      setError(apiError(e));
      setSaveOpen(false);
    }
  };

  const capa = async (mode: "FROM_AI" | "MANUAL") => {
    setBusy(true);
    try {
      const created = await createCapa(findingId, mode);
      navigate(`/capa/${created.capa.id}`);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await commentFinding(findingId, comment.trim());
      setComment("");
      await load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const reasonRequired = (a: FindingAction) => a === "REJECT" || a === "CLOSE" || a === "REOPEN";
  const actionButton = (a: FindingAction) => (
    <button key={a} className={`aces-btn ${a === "REJECT" ? "danger" : a === "ACCEPT" ? "success" : ""}`} disabled={busy}
      onClick={() => (a === "CLAIM" ? run(a) : setPending(a))}>
      {t(`aces.finding.actions.${a}`)}
    </button>
  );

  const Field: React.FC<{ label: string; field?: string; value: any; children?: React.ReactNode }> = ({ label, field, value, children }) => (
    <div className="aces-field">
      <label>{label}</label>
      <div className={`value ${field && changedFromAi(field, value) ? "changed" : ""}`}>
        {field && changedFromAi(field, value) && <span className="aces-chip amber aces-modified">{t("aces.finding.modifiedBadge")}</span>}
        {children ?? (value === null || value === "" || value === undefined ? "—" : String(value))}
      </div>
    </div>
  );

  const revisionValue = (field: string, value: string | null) => {
    if (value == null || value === "") return "—";
    if (FIELD_ENUM[field]) return t(`aces.enum.${FIELD_ENUM[field]}.${value}`);
    if (value === "true" || value === "false") return t(value === "true" ? "aces.common.yes" : "aces.common.no");
    if (field === "cargoJourneyStageId") return stageName(Number(value)) || value;
    if (field === "ccpId") return ccpName(Number(value)) || value;
    if (field === "acesClauseId") return clauseName(Number(value)) || value;
    return value;
  };

  const stageName = (sid: number | null) => catalogue?.stages.find((s) => s.id === sid)?.name;
  const ccpName = (cid: number | null) => catalogue?.ccps.find((c) => c.id === cid)?.code;
  const clauseName = (cid: number | null) => catalogue?.clauses.find((c) => c.id === cid)?.refCode;

  return (
    <PageShell
      title={<>{t("aces.titles.finding")} <span className="aces-muted" style={{ fontSize: 16 }}>{f.findingRef}</span></>}
      crumbs={[
        { label: t("aces.nav.reports"), to: "/submittedChecklist" },
        { label: `${t("aces.titles.aiAnalysisCrumb")} ${detail.analysisRef}`, to: `/ai/analysis/${f.analysisId}` },
        { label: `${t("aces.titles.findingCrumb")} ${f.findingRef}` },
      ]}
      actions={<button className="aces-btn secondary" onClick={() => navigate(`/ai/analysis/${f.analysisId}`)}><i className="ri-arrow-left-line"></i> {detail.analysisRef}</button>}
    >
      <ErrorAlert message={error} onClose={() => setError(null)} />
      {(f.isUrgent || previewUrgent) && <UrgentBanner />}
      {["AI_PROPOSED", "UNDER_REVIEW"].includes(f.reviewStatus) && <AiNotice />}

      <div className="aces-card">
        <div className="aces-actions" style={{ justifyContent: "space-between" }}>
          <div className="aces-actions">
            <ClassificationChip value={edit?.classification || f.classification} />
            <RiskChip level={preview.level} score={preview.score} />
            <ReviewChip value={f.reviewStatus} />
            {f.capaRequired && <span className="aces-chip grey">{t("aces.finding.capaRequired")}</span>}
          </div>
          {!edit && <div className="aces-actions">
            {detail.canEdit && (
              <button className="aces-btn secondary" onClick={() => setEdit(fromDetail(detail))}><i className="ri-edit-line"></i> {t("aces.finding.edit")}</button>
            )}
            {detail.allowedActions.map(actionButton)}
            {detail.canCreateCapa && (
              <>
                {detail.capaProposal && <button className="aces-btn" disabled={busy} onClick={() => capa("FROM_AI")}><i className="ri-tools-line"></i> {t("aces.finding.generateCapa")}</button>}
                <button className="aces-btn secondary" disabled={busy} onClick={() => capa("MANUAL")}>{t("aces.finding.manualCapa")}</button>
              </>
            )}
          </div>}
        </div>
        <p className="aces-muted mt-2 mb-0">
          {detail.checklist.referenceNo} — {detail.checklist.title}
          {f.reviewedByUserId && <> · {t("aces.finding.reviewedAt")} {fmtDate(f.reviewedAt)}</>}
          {detail.capas.map((c) => (
            <span key={c.id} style={{ marginLeft: 10 }}>
              <button type="button" className="aces-table-link" onClick={() => navigate(`/capa/${c.id}`)}>{c.capaRef}</button> <CapaChip value={c.status} />
            </span>
          ))}
        </p>
        {f.capaRequired && ["ACCEPTED", "MODIFIED"].includes(f.reviewStatus) && !detail.readyToClose && (
          <p className="aces-muted mb-0">{t("aces.finding.closeNeedsCapa")}</p>
        )}
      </div>

      {edit ? (
        <div className="aces-card">
          <h5>{t("aces.finding.editTitle")}</h5>
          <div className="aces-grid-3">
            <div className="aces-field">
              <label htmlFor="finding-edit-1">{t("aces.finding.classification")}</label>
              <select id="finding-edit-1" className="form-control" value={edit.classification} onChange={(e) => setEdit({ ...edit, classification: e.target.value as Classification })}>
                {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{t(`aces.enum.classification.${c}`)}</option>)}
              </select>
            </div>
            <div className="aces-field">
              <label htmlFor="finding-edit-2">{t("aces.finding.likelihood")}</label>
              <select id="finding-edit-2" className="form-control" value={edit.likelihood ?? ""} onChange={(e) => setEdit({ ...edit, likelihood: e.target.value ? Number(e.target.value) : null })}>
                <option value="">{t("aces.finding.cannotDetermine")}</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} — {t(`aces.scale.likelihood.${n}`)}</option>)}
              </select>
            </div>
            <div className="aces-field">
              <label htmlFor="finding-edit-3">{t("aces.finding.severity")}</label>
              <select id="finding-edit-3" className="form-control" value={edit.severity ?? ""} onChange={(e) => setEdit({ ...edit, severity: e.target.value ? Number(e.target.value) : null })}>
                <option value="">{t("aces.finding.cannotDetermine")}</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} — {t(`aces.scale.severity.${n}`)}</option>)}
              </select>
            </div>
            <div className="aces-field">
              <label htmlFor="finding-edit-4">{t("aces.finding.evidenceStatus")}</label>
              <select id="finding-edit-4" className="form-control" value={edit.evidenceStatus} onChange={(e) => setEdit({ ...edit, evidenceStatus: e.target.value as EvidenceStatus })}>
                {EVIDENCE_STATUSES.map((c) => <option key={c} value={c}>{t(`aces.enum.evidence.${c}`)}</option>)}
              </select>
            </div>
            <div className="aces-field">
              <label htmlFor="finding-edit-5">{t("aces.finding.productIntegrity")}</label>
              <select id="finding-edit-5" className="form-control" value={edit.productIntegrityImpact ?? ""} onChange={(e) => setEdit({ ...edit, productIntegrityImpact: (e.target.value || null) as ProductIntegrityImpact | null })}>
                <option value="">—</option>
                {IMPACTS.map((c) => <option key={c} value={c}>{t(`aces.enum.impact.${c}`)}</option>)}
              </select>
            </div>
            <div className="aces-field">
              <label>{t("aces.finding.riskPreview")}</label>
              <div><RiskChip level={preview.level} score={preview.score} /> <span className="aces-muted">{t("aces.finding.riskComputed")}</span></div>
            </div>
            {catalogue && (
              <>
                <div className="aces-field">
                  <label htmlFor="finding-edit-6">{t("aces.finding.stage")}</label>
                  <select id="finding-edit-6" className="form-control" value={edit.cargoJourneyStageId ?? ""} onChange={(e) => setEdit({ ...edit, cargoJourneyStageId: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">—</option>
                    {catalogue.stages.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
                  </select>
                </div>
                <div className="aces-field">
                  <label htmlFor="finding-edit-7">{t("aces.finding.ccp")}</label>
                  <select id="finding-edit-7" className="form-control" value={edit.ccpId ?? ""} onChange={(e) => setEdit({ ...edit, ccpId: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">—</option>
                    {catalogue.ccps.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                  </select>
                </div>
                <div className="aces-field">
                  <label htmlFor="finding-edit-8">{t("aces.finding.acesReference")}</label>
                  <select id="finding-edit-8" className="form-control" value={edit.acesClauseId ?? ""} onChange={(e) => setEdit({ ...edit, acesClauseId: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">—</option>
                    {catalogue.clauses.map((c) => <option key={c.id} value={c.id}>{c.refCode} — {c.title}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
          {([
            ["statement", "aces.finding.statement"],
            ["applicableRequirement", "aces.finding.requirement"],
            ["objectiveEvidence", "aces.finding.objectiveEvidence"],
            ["productIntegrityDescription", "aces.finding.productIntegrityDescription"],
            ["immediateContainment", "aces.finding.containment"],
            ["recommendedAction", "aces.finding.recommendedAction"],
            ["correctiveAction", "aces.capa.correctiveAction"],
            ["preventiveAction", "aces.capa.preventiveAction"],
          ] as [keyof EditState, string][]).map(([k, label]) => (
            <div className="aces-field" key={k}>
              <label htmlFor={`finding-edit-${k}`}>{t(label)}</label>
              <textarea id={`finding-edit-${k}`} className="form-control" rows={2} value={(edit[k] as string) || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
            </div>
          ))}
          <label className="aces-check" htmlFor="capaRequired">
            <input type="checkbox" id="capaRequired" checked={edit.capaRequired} onChange={(e) => setEdit({ ...edit, capaRequired: e.target.checked })} />
            {t("aces.finding.capaRequired")}
          </label>
          <div className="aces-actions">
            <button className="aces-btn" disabled={Object.keys(changes).length === 0} onClick={() => setSaveOpen(true)}>{t("aces.common.save")}</button>
            <button className="aces-btn secondary" onClick={() => setEdit(null)}>{t("common.cancel")}</button>
          </div>
        </div>
      ) : (
        <div className="aces-card">
          <div className="aces-compare">
            <div className={`ai-col ${rejected ? "rejected" : ""}`}>
              <div className="col-title">
                <i className="ri-sparkling-2-line" aria-hidden="true"></i> {t("aces.finding.aiOriginal")}
                {rejected && <span className="aces-chip red ms-2">{t("aces.finding.rejectedAi")}</span>}
              </div>
              {detail.aiOriginal ? (
                <>
                  <Field label={t("aces.finding.classification")} value={t(`aces.enum.classification.${ai.classification}`)} />
                  <Field label={t("aces.finding.statement")} value={ai.finding_statement} />
                  <Field label={t("aces.finding.requirement")} value={ai.applicable_requirement} />
                  <Field label={t("aces.finding.objectiveEvidence")} value={ai.objective_evidence} />
                  <Field label={t("aces.finding.evidenceStatus")} value={t(`aces.enum.evidence.${ai.evidence_status}`)} />
                  <Field label={t("aces.finding.risk")} value={`L${ai.likelihood ?? "—"} × S${ai.severity ?? "—"} = ${ai.computed_risk_score ?? t("aces.enum.risk.NOT_DETERMINED")}`} />
                  <Field label={t("aces.finding.likelihoodRationale")} value={ai.likelihood_rationale} />
                  <Field label={t("aces.finding.severityRationale")} value={ai.severity_rationale} />
                  <Field label={t("aces.finding.containment")} value={ai.immediate_containment} />
                  <Field label={t("aces.finding.recommendedAction")} value={ai.recommended_action} />
                </>
              ) : <p className="aces-muted">{t("aces.finding.noAiOriginal")}</p>}
            </div>
            <div className={`human-col ${approved ? "approved" : ""}`}>
              <div className="col-title">
                <i className={approved ? "ri-shield-check-line" : "ri-user-line"} aria-hidden="true"></i>{" "}
                {t(approved ? "aces.finding.approved" : rejected ? "aces.finding.rejectedAi" : "aces.finding.current")}
              </div>
              <Field label={t("aces.finding.classification")} field="classification" value={f.classification}>{t(`aces.enum.classification.${f.classification}`)}</Field>
              <Field label={t("aces.finding.statement")} field="statement" value={f.statement} />
              <Field label={t("aces.finding.requirement")} field="applicableRequirement" value={f.applicableRequirement} />
              <Field label={t("aces.finding.objectiveEvidence")} field="objectiveEvidence" value={f.objectiveEvidence} />
              <Field label={t("aces.finding.evidenceStatus")} field="evidenceStatus" value={f.evidenceStatus}>{t(`aces.enum.evidence.${f.evidenceStatus}`)}</Field>
              <Field label={t("aces.finding.risk")} value={null}
                field={detail.aiOriginal && (ai.likelihood !== f.likelihood || ai.severity !== f.severity) ? "__changed" : undefined}>
                L{f.likelihood ?? "—"} × S{f.severity ?? "—"} = <RiskChip level={f.riskLevel} score={f.riskScore} />
              </Field>
              <Field label={t("aces.finding.stage")} value={detail.stage || stageName(f.cargoJourneyStageId)} />
              <Field label={t("aces.finding.ccp")} value={detail.ccp || ccpName(f.ccpId)} />
              <Field label={t("aces.finding.acesReference")} value={detail.acesClause || clauseName(f.acesClauseId) || t(detail.legacyAces ? "aces.finding.notMappedLegacy" : "aces.finding.notMapped")} />
              <Field label={t("aces.finding.productIntegrity")} value={null}>
                {f.productIntegrityImpact ? t(`aces.enum.impact.${f.productIntegrityImpact}`) : "—"}{f.productIntegrityDescription ? ` — ${f.productIntegrityDescription}` : ""}
              </Field>
              <Field label={t("aces.finding.containment")} field="immediateContainment" value={f.immediateContainment} />
              <Field label={t("aces.finding.possibleCauses")} value={null}>
                {detail.possibleCauses?.length ? <ul className="mb-0">{detail.possibleCauses.map((c, i) => <li key={i}>{c.hypothesis} <span className="aces-muted">— {c.verification_needed}</span></li>)}</ul> : "—"}
              </Field>
              <Field label={t("aces.finding.recommendedAction")} field="recommendedAction" value={f.recommendedAction} />
              {detail.confirmationsNeeded && detail.confirmationsNeeded.length > 0 && (
                <Field label={t("aces.finding.confirmations")} value={null}><ul className="mb-0">{detail.confirmationsNeeded.map((c, i) => <li key={i}>{c}</li>)}</ul></Field>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="aces-card">
        <h5>{t("aces.finding.sourceQuestions")}</h5>
        {detail.questions.map((q) => (
          <div key={q.questionId} className="mb-3">
            <div><b>{q.title}</b> — {q.answer || "—"}{q.reason && <span className="aces-muted"> ({q.reason})</span>}</div>
            {q.evidenceIds.length > 0 && <div className="aces-photos mt-1">{q.evidenceIds.map((eid) => <SecureImage key={eid} fileId={eid} alt={detail.evidence.find((e) => e.id === eid)?.code || ""} />)}</div>}
          </div>
        ))}
        {(() => {
          // Only evidence the AI linked that is not already shown under a source question.
          const shown = new Set(detail.questions.flatMap((q) => q.evidenceIds));
          const extra = detail.evidence.filter((e) => e.mime.startsWith("image/") && !shown.has(e.id));
          return extra.length > 0 && (
            <>
              <div className="aces-muted">{t("aces.finding.linkedEvidence")}</div>
              <div className="aces-photos mt-1">{extra.map((e) => <SecureImage key={e.id} fileId={e.id} alt={e.code || ""} />)}</div>
            </>
          );
        })()}
      </div>

      {detail.revisions.length > 0 && (
        <div className="aces-card">
          <h5>{t("aces.finding.revisions")}</h5>
          <div className="__defaultTable">
            <table>
              <thead>
                <tr>
                  <th>{t("aces.audit.field")}</th>
                  <th>{t("aces.finding.aiOriginal")}</th>
                  <th>{t("aces.audit.previous")}</th>
                  <th>{t("aces.finding.humanFinal")}</th>
                  <th>{t("aces.audit.user")}</th>
                  <th>{t("aces.audit.when")}</th>
                  <th>{t("aces.audit.reason")}</th>
                </tr>
              </thead>
              <tbody>
                {detail.revisions.map((r) => (
                  <tr key={r.id}>
                    <td>{FIELD_LABEL[r.field] ? t(FIELD_LABEL[r.field]) : r.field}</td>
                    <td className="aces-preline">{revisionValue(r.field, r.aiOriginalValue)}</td>
                    <td className="aces-preline">{revisionValue(r.field, r.previousValue)}</td>
                    <td className="aces-preline">{revisionValue(r.field, r.newValue)}</td>
                    <td>{r.userName}</td>
                    <td>{fmtDate(r.changedAt)}</td>
                    <td className="aces-preline">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="aces-grid-2">
        <div className="aces-card">
          <h5>{t("aces.finding.history")}</h5>
          <ul className="aces-timeline">
            {detail.history.map((h) => (
              <li key={h.id}>
                <b>{t(`aces.enum.review.${h.toStatus}`)}</b> · {fmtDate(h.changedAt)} · {h.userName || t("aces.nav.ai")}
                {h.reason && <div className="aces-muted">{h.reason}</div>}
              </li>
            ))}
          </ul>
        </div>
        <div className="aces-card">
          <h5>{t("aces.finding.comments")}</h5>
          {detail.comments.map((c) => (
            <div key={c.id} className="mb-2"><b>{c.userName}</b> <span className="aces-muted">{fmtDate(c.createdAt)}</span><div className="aces-preline">{c.text}</div></div>
          ))}
          <div className="aces-actions mt-2">
            <input className="form-control" style={{ flex: 1 }} aria-label={t("aces.finding.addComment")} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("aces.finding.addComment")} />
            <button className="aces-btn" onClick={sendComment} disabled={!comment.trim()}>{t("aces.common.add")}</button>
          </div>
        </div>
      </div>

      <div className="aces-card">
        <div className="aces-actions" style={{ justifyContent: "space-between" }}>
          <h5 className="mb-0">{t("aces.audit.title")}</h5>
          {audit === null && <button className="aces-btn small secondary" onClick={() => getEntityAudit("FINDING", findingId).then(setAudit).catch((e) => setError(apiError(e)))}>{t("aces.common.show")}</button>}
        </div>
        {audit && <div className="mt-2"><AuditTable entries={audit} /></div>}
      </div>

      <ReasonModal show={saveOpen} title={t("aces.finding.saveTitle")} onCancel={() => setSaveOpen(false)} onConfirm={save}>
        <ul className="aces-muted">{Object.keys(changes).map((k) => <li key={k}>{k}</li>)}</ul>
      </ReasonModal>
      <ReasonModal show={pending !== null} title={pending ? t(`aces.finding.actions.${pending}`) : ""} danger={pending === "REJECT"}
        required={pending !== null && reasonRequired(pending)} confirmLabel={pending ? t(`aces.finding.actions.${pending}`) : undefined}
        onCancel={() => setPending(null)} onConfirm={(reason) => run(pending!, reason || undefined)}>
        {pending === "ACCEPT" && <p className="aces-muted">{t("aces.finding.acceptHint")}</p>}
      </ReasonModal>
    </PageShell>
  );
};

export default FindingScreen;
